/**
 * Admin theme factory.
 *
 * The corporate identity lives in a compact token set (`qrPhoneTokens`, in
 * qrPhoneTheme.ts: ink + Live Green, Space Grotesk); this factory turns the
 * shared layout/component system into a `createAdminTheme(tokens)` call, so
 * the identity stays tunable without touching a 900-line theme file.
 *
 * The component overrides, radii, rail treatment and es-ES locale live here;
 * only the values in `BrandTokens` change with the identity.
 *
 * CUSTOMIZATION GUIDE:
 * 1. Change brand colours/fonts -> edit the token set (qrPhoneTheme.ts),
 *    NOT this factory.
 * 2. Change layout/component behaviour -> edit this factory.
 */

import { defaultTheme } from 'react-admin';
import { createTheme, Palette, Theme, PaletteOptions } from '@mui/material/styles';
import { esES } from '@mui/material/locale';

// ============================================
// TYPE DECLARATIONS
// ============================================

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      radius: Record<string, string>;
      shadows: Record<string, string>;
      focusRing: string;
      /** Solid-button fill — shared by MuiButton (contained) and the avatar. */
      ctaFill: string;
      ctaFillHover: string;
      login: {
        containerGradient: (palette: Palette) => string;
        topBarGradient: (palette: Palette) => string;
        fieldBackground: (palette: Palette) => string;
        logoFilter: string;
      };
      layout: Record<string, any>;
      userMenu: Record<string, any>;
      userAvatar: Record<string, any>;
      menu: Record<string, any>;
      text: Record<string, string>;
      table: Record<string, any>;
    };
    /** react-admin's own <Sidebar> reads this to size its Drawer. */
    sidebar: {
      width: number;
      closedWidth: number;
    };
  }

  interface ThemeOptions {
    custom?: {
      radius?: Record<string, string>;
      shadows?: Record<string, string>;
      focusRing?: string;
      ctaFill?: string;
      ctaFillHover?: string;
      login?: {
        containerGradient?: (palette: Palette) => string;
        topBarGradient?: (palette: Palette) => string;
        fieldBackground?: (palette: Palette) => string;
        logoFilter?: string;
      };
      layout?: Record<string, any>;
      userMenu?: Record<string, any>;
      userAvatar?: Record<string, any>;
      menu?: Record<string, any>;
      text?: Record<string, string>;
      table?: Record<string, any>;
    };
    sidebar?: {
      width?: number;
      closedWidth?: number;
    };
  }

  // React-admin component overrides
  interface Components {
    RaList?: { styleOverrides?: Record<string, any> };
    RaTopToolbar?: { styleOverrides?: Record<string, any> };
    RaDatagrid?: { styleOverrides?: Record<string, any> };
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    /** CTA inside a TopToolbar that creates a new resource. Same fill as
     *  the contained primary, kept as a named variant so consumer apps
     *  can express intent ("create resource") in JSX. */
    createButton: true;
    /** Subtle inline action — ghost button. Equivalent to MUI text
     *  variant but with consistent hover affordance. */
    ghost: true;
    /** Destructive action — red fill, used for delete confirmations
     *  and similar irreversible operations. */
    danger: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    /** Intense solid-accent chip — a single per-view emphasis pill. Never for
     *  repeated status tints in dense tables (those use the tinted-outline
     *  colorX styles); reserved for one standout element per screen. */
    solid: true;
  }
}

// ============================================
// BRAND TOKENS — the only per-brand surface
// ============================================

export interface BrandTokens {
  /** Stable brand key. */
  brand: string;
  /** UI font stack (heading + body). */
  fontFamily: string;
  /** Full MUI palette (primary/secondary/status/background/grey/text/divider). */
  palette: PaletteOptions;
  /** Solid-button fill + hover (contained/createButton/dialog-contained). */
  ctaFill: string;
  ctaFillHover: string;
  /** Text colour that reads on the CTA fill (ink on green vs. white on teal-ink). */
  containedText: string;
  /** :focus-visible halo (brand primary at low alpha). */
  focusRing: string;
  /** Dark thin outline driving the datagrid header line + neutral chips. */
  borderStrong: string;
  /** Soft in-table row separator. */
  borderRow: string;
  /** Card outline colour. */
  cardBorder: string;
  /** Card elevation (none for QR Phone's flat outline, soft warm shadow otherwise). */
  cardShadow: string;
  /** Datagrid frame outline (may differ from cardBorder). */
  datagridFrameBorder: string;
  /** Destructive-button hover fill (a shade darker than palette.error.dark). */
  dangerHover: string;
  /** Profile-rail avatar disc. */
  avatarBg: string;
  avatarColor: string;
  /** Micro text tones not modelled by MUI's two-tier text.primary/secondary. */
  textMicro: { tertiary: string; label: string; idMono: string };
  /** Dense-table header fill + label colour. */
  tableHeaderBg: string;
  headCellColor: string;
  /** Popover/menu elevation set (warm vs. cool ink). */
  shadows: {
    none: string;
    subtle: string;
    medium: string;
    strong: string;
    login: string;
  };
}

// Radii, rail geometry and layout are brand-invariant.
const radius = {
  small: '6px',
  medium: '7px',
  large: '10px',
  card: '10px',
  chip: '9999px',
  full: '9999px',
};

// ============================================
// THEME FACTORY
// ============================================

export function createAdminTheme(t: BrandTokens): Theme {
  const borderStrong = t.borderStrong;
  const borderRow = t.borderRow;
  const cardBorder = t.cardBorder;
  const cardShadow = t.cardShadow;
  const CTA_FILL = t.ctaFill;
  const CTA_FILL_HOVER = t.ctaFillHover;
  const CONTAINED_TEXT = t.containedText;

  const custom = {
    radius,
    shadows: t.shadows,
    focusRing: t.focusRing,
    ctaFill: CTA_FILL,
    ctaFillHover: CTA_FILL_HOVER,
    login: {
      containerGradient: (palette: Palette) =>
        `linear-gradient(135deg, ${palette.grey[50]} 0%, ${palette.grey[200]} 100%)`,
      topBarGradient: (palette: Palette) =>
        `linear-gradient(90deg, ${palette.primary.main} 0%, ${palette.primary.light} 100%)`,
      fieldBackground: (palette: Palette) => palette.grey[50],
      logoFilter: 'drop-shadow(0 4px 6px rgb(0 0 0 / 0.1))',
    },
    layout: {
      desktopContainerPadding: '8px 8px 8px 8px',
      gap: '0px',
      mobileContainerPadding: '4px',
      mobileTopMargin: '16px',
    },
    userMenu: {
      paper: {
        width: '280px',
        borderRadius: radius.large,
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      },
    },
    userAvatar: {
      size: '40px',
      bg: t.avatarBg,
      color: t.avatarColor,
      iconSize: '24px',
    },
    // Micro-tones not modeled by MUI's two-tier text.primary/secondary (page
    // subtitle, table header labels, monospace IDs).
    text: t.textMicro,
    // Dense table surfaces (list tables) — hairline borders instead of shadows.
    table: {
      headerBg: t.tableHeaderBg,
      headerBorder: borderStrong,
      rowBorder: borderRow,
      headCell: {
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: 1.3,
        textTransform: 'none' as const,
        color: t.headCellColor,
      },
    },
    // Rail: a flush, solid docked sidebar (not a floating card) — items read in
    // white/translucent-white. The rail fill is palette.background.menu (single
    // source of truth, per brand).
    menu: {
      openWidth: '230px',
      closedWidth: '64px',
      border: 'rgba(0,0,0,0.08)',
      groupLabelColor: 'rgba(255,255,255,0.55)',
      item: {
        borderRadius: radius.small,
        margin: '2px 0',
        padding: '7px 8px',
        gap: '12px',
        fontSize: '14px',
        fontWeight: 500,
        color: () => 'rgba(255,255,255,0.85)',
        iconColor: () => 'rgba(255,255,255,0.7)',
        hover: {
          color: () => '#FFFFFF',
          backgroundColor: () => 'rgba(255,255,255,0.08)',
        },
        active: {
          color: () => '#FFFFFF',
          backgroundColor: () => 'rgba(255,255,255,0.16)',
          fontWeight: 600,
          marker: () => '#FFFFFF',
          hover: {
            backgroundColor: () => 'rgba(255,255,255,0.22)',
          },
        },
        closedActiveIcon: {
          backgroundColor: () => 'rgba(255,255,255,0.16)',
          color: () => '#FFFFFF',
          borderRadius: '6px',
        },
      },
    },
  };

  const baseTheme = createTheme({
    ...defaultTheme,
    palette: t.palette,
    custom,
    sidebar: {
      width: 230,
      closedWidth: 64,
    },
    typography: {
      fontFamily: t.fontFamily,
      fontSize: 14,
      htmlFontSize: 16,
      h1: { fontSize: '1.625rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.015em' },
      h3: { fontSize: '1.1875rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em' },
      h4: { fontSize: '1.0625rem', fontWeight: 500, lineHeight: 1.4 },
      h5: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.45 },
      h6: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.45, letterSpacing: '0.01em' },
      body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.55 },
      caption: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5 },
      button: { fontWeight: 500, letterSpacing: '0.01em' },
    },
  });

  /* The theme is built then re-wrapped with the es-ES locale so MUI's built-in
     strings (pagination, date pickers, tables, a11y labels) ship in formal
     Spanish out of the box. */
  return createTheme({
    ...baseTheme,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '.RaList-main': {
            '& .RaList-actions': {
              borderBottom: `1px solid ${baseTheme.palette.divider}`,
            },
          },
          '*:focus-visible': {
            outline: 'none',
            boxShadow: custom.focusRing,
            borderRadius: radius.small,
          },
          'button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible': {
            outline: 'none',
            boxShadow: custom.focusRing,
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${baseTheme.palette.divider}`,
          },
        },
      },

      MuiButton: {
        defaultProps: {
          size: 'small' as const,
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: 500,
            borderRadius: radius.medium,
            textTransform: 'none',
            minHeight: '38px',
            padding: '8px 14px',
            boxShadow: 'none',
            transition: 'background-color 0.3s ease, color 0.3s ease',
            '&:hover': { boxShadow: 'none' },
          },
          // Solid CTA — brand CTA fill with the brand's on-fill text colour.
          contained: {
            backgroundColor: CTA_FILL,
            color: CONTAINED_TEXT,
            boxShadow: 'none',
            '&:hover': { backgroundColor: CTA_FILL_HOVER, boxShadow: 'none' },
          },
          // Secondary button — white fill + neutral border + dark text.
          outlined: {
            backgroundColor: '#FFFFFF',
            borderColor: baseTheme.palette.divider,
            color: baseTheme.palette.text.primary,
            '&:hover': {
              backgroundColor: baseTheme.palette.action.hover,
              borderColor: baseTheme.palette.divider,
            },
          },
        },
        variants: [
          {
            props: { variant: 'createButton' },
            style: {
              backgroundColor: CTA_FILL,
              color: CONTAINED_TEXT,
              boxShadow: 'none',
              padding: '8px 14px',
              fontWeight: 500,
              fontSize: '1rem',
              minWidth: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              '&:hover': {
                backgroundColor: CTA_FILL_HOVER,
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.25rem',
                color: CONTAINED_TEXT,
              },
            },
          },
          {
            props: { variant: 'ghost' },
            style: {
              backgroundColor: 'transparent',
              color: baseTheme.palette.text.primary,
              boxShadow: 'none',
              padding: '6px 12px',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: baseTheme.palette.action.hover,
              },
            },
          },
          {
            props: { variant: 'danger' },
            style: {
              backgroundColor: baseTheme.palette.error.dark,
              color: '#FFFFFF',
              boxShadow: 'none',
              padding: '6px 16px',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: t.dangerHover,
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.25rem',
                color: '#FFFFFF',
              },
            },
          },
        ],
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            border: `1px solid ${baseTheme.palette.primary.dark}20`,
            borderRadius: radius.small,
            color: baseTheme.palette.primary.main,
            width: 36,
            height: 32,
            '&:hover': {
              backgroundColor: baseTheme.palette.primary.dark,
              color: baseTheme.palette.common.white,
            },
          },
          colorInherit: {
            color: baseTheme.palette.text.secondary,
            border: `1px solid ${baseTheme.palette.text.secondary}20`,
            '&:hover': {
              color: baseTheme.palette.primary.main,
              backgroundColor: baseTheme.palette.action.hover,
              border: `1px solid ${baseTheme.palette.primary.main}30`,
            },
          },
          colorPrimary: {
            color: baseTheme.palette.primary.main,
            border: `1px solid ${baseTheme.palette.primary.main}30`,
            '&:hover': { backgroundColor: `${baseTheme.palette.primary.main}14` },
          },
          colorSecondary: {
            color: baseTheme.palette.secondary.main,
            border: `1px solid ${baseTheme.palette.secondary.main}30`,
            '&:hover': { backgroundColor: `${baseTheme.palette.secondary.main}14` },
          },
          colorError: {
            color: baseTheme.palette.error.main,
            border: `1px solid ${baseTheme.palette.error.main}30`,
            '&:hover': { backgroundColor: `${baseTheme.palette.error.main}14` },
          },
          colorWarning: {
            color: baseTheme.palette.warning.main,
            border: `1px solid ${baseTheme.palette.warning.main}30`,
            '&:hover': { backgroundColor: `${baseTheme.palette.warning.main}14` },
          },
          colorInfo: {
            color: baseTheme.palette.info.main,
            border: `1px solid ${baseTheme.palette.info.main}30`,
            '&:hover': { backgroundColor: `${baseTheme.palette.info.main}14` },
          },
          colorSuccess: {
            color: baseTheme.palette.success.main,
            border: `1px solid ${baseTheme.palette.success.main}30`,
            '&:hover': { backgroundColor: `${baseTheme.palette.success.main}14` },
          },
          sizeLarge: { width: 48, height: 48 },
          sizeSmall: { width: 32, height: 32 },
        },
      },

      MuiBadge: {
        defaultProps: {
          color: 'primary',
          max: 99,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        },
        styleOverrides: {
          badge: ({ theme }) => ({
            borderRadius: theme.custom?.radius?.chip ?? '4px',
            minWidth: 18,
            height: 18,
            padding: '0 4px',
            border: `2px solid ${theme.palette.common.white}`,
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.common.white,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            lineHeight: 1,
          }),
        },
      },

      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px',
            fontWeight: 500,
            minWidth: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            '& .MuiButton-text': {
              backgroundColor: baseTheme.palette.grey[100],
              color: baseTheme.palette.grey[600],
              border: `1px solid ${baseTheme.palette.divider}`,
              '&:hover': { backgroundColor: baseTheme.palette.grey[200] },
            },
            '& .MuiButton-contained': {
              backgroundColor: CTA_FILL,
              color: CONTAINED_TEXT,
              border: `1px solid ${CTA_FILL}`,
              '&:hover': {
                backgroundColor: CTA_FILL_HOVER,
                borderColor: CTA_FILL_HOVER,
              },
            },
            '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
          },
        },
      },

      RaList: {
        styleOverrides: {
          root: {
            '& .RaList-main': {
              paddingTop: 16,
              paddingLeft: 16,
              paddingRight: 16,
            },
          },
        },
      },

      RaTopToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            marginBottom: baseTheme.spacing(2),
          },
        },
      },

      RaDatagrid: {
        styleOverrides: {
          root: {
            backgroundColor: baseTheme.palette.background.paper,
            border: `1px solid ${t.datagridFrameBorder}`,
            borderRadius: radius.card,
            overflow: 'hidden',

            '& .RaDatagrid-headerCell': {
              backgroundColor: custom.table.headerBg,
              borderBottom: `1px solid ${borderStrong}`,
              padding: '12px',
              ...custom.table.headCell,
            },

            '& .RaDatagrid-row': {
              backgroundColor: baseTheme.palette.common.white,
              transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s',
              '&:hover': { backgroundColor: baseTheme.palette.action.hover },
            },

            '& .RaDatagrid-rowCell': {
              fontSize: '0.875rem',
              padding: '10px 12px',
              borderBottom: `1px solid ${borderRow}`,
              color: baseTheme.palette.text.primary,
              '&:last-of-type': { borderRight: 'none' },
            },

            'tbody tr:last-child .RaDatagrid-rowCell': {
              borderBottom: 'none',
            },

            '& .MuiTablePagination-root': {
              borderTop: `1px solid ${baseTheme.palette.divider}`,
              backgroundColor: baseTheme.palette.grey[50],
              padding: baseTheme.spacing(1),
            },

            '& .RaDatagrid-rowCell:has(button)': {
              paddingTop: '6px',
              paddingBottom: '6px',
              textAlign: 'right',
              whiteSpace: 'nowrap',
            },

            '& button.RaButton-link': {
              fontSize: '0.8rem',
              color: baseTheme.palette.text.secondary,
              padding: '4px 8px',
              borderRadius: radius.small,
              transition: 'color 0.2s ease, background-color 0.2s ease',
              '&:hover': {
                color: baseTheme.palette.primary.main,
                backgroundColor: `${baseTheme.palette.primary.light}10`,
              },
            },

            '& button[aria-label="Delete"]': {
              color: baseTheme.palette.primary.main,
              width: '36',
              '&:hover': {
                color: baseTheme.palette.common.white,
                backgroundColor: baseTheme.palette.error.main,
              },
            },
          },
        },
      },

      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: baseTheme.palette.common.white,
            borderRadius: radius.card,
            boxShadow: cardShadow,
            border: `1px solid ${cardBorder}`,
            marginBottom: baseTheme.spacing(2),
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: { padding: '12px 16px' },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.chip,
            fontWeight: 500,
            backgroundColor: '#F1EDE6',
            color: '#2C2926',
            border: `1px solid ${borderStrong}`,
          },
          colorSecondary: {
            backgroundColor: '#F1EDE6',
            color: '#2C2926',
            border: `1px solid ${borderStrong}`,
          },
          colorPrimary: {
            backgroundColor: `${baseTheme.palette.primary.main}14`,
            border: `1px solid ${baseTheme.palette.primary.dark}`,
            color: baseTheme.palette.primary.dark,
          },
          colorSuccess: {
            backgroundColor: `${baseTheme.palette.success.main}14`,
            border: `1px solid ${baseTheme.palette.success.dark}`,
            color: baseTheme.palette.success.dark,
          },
          colorWarning: {
            backgroundColor: `${baseTheme.palette.warning.main}1F`,
            border: `1px solid ${baseTheme.palette.warning.dark}`,
            color: baseTheme.palette.warning.dark,
          },
          colorError: {
            backgroundColor: `${baseTheme.palette.error.main}14`,
            border: `1px solid ${baseTheme.palette.error.dark}`,
            color: baseTheme.palette.error.dark,
          },
          colorInfo: {
            backgroundColor: `${baseTheme.palette.info.main}14`,
            border: `1px solid ${baseTheme.palette.info.dark}`,
            color: baseTheme.palette.info.dark,
          },
        },
        variants: [
          {
            props: { variant: 'solid' },
            style: {
              border: 'none',
              backgroundColor: baseTheme.palette.primary.main,
              color: baseTheme.palette.primary.contrastText,
              fontWeight: 500,
            },
          },
        ],
      },

      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': { minHeight: '48px' },
          },
        },
      },

      MuiSelect: {
        styleOverrides: {
          select: {
            display: 'flex',
            alignItems: 'center',
            minHeight: '22px',
            paddingTop: '8.5px',
            paddingBottom: '8.5px',
          },
          icon: { top: 'calc(50% - 12px)' },
        },
      },

      MuiInputBase: {
        styleOverrides: {
          root: {
            '&.MuiInputBase-sizeSmall': { minHeight: '48px' },
            '&:not(.MuiInputBase-sizeSmall)': { minHeight: '56px' },
          },
          input: {
            '&.MuiInputBase-inputSizeSmall': {
              paddingTop: '8.5px',
              paddingBottom: '8.5px',
            },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.MuiInputLabel-sizeSmall': {
              transform: 'translate(14px, 9px) scale(1)',
            },
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
            },
          },
        },
      },

      MuiFormControl: {
        styleOverrides: {
          root: { marginTop: 0, marginBottom: 0 },
        },
      },

      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            paddingTop: '4px',
            paddingBottom: '4px',
            '&.MuiInputBase-sizeSmall': {
              paddingTop: '2px',
              paddingBottom: '2px',
              '& .MuiAutocomplete-input': {
                paddingTop: '6.5px',
                paddingBottom: '6.5px',
              },
            },
          },
        },
      },

      MuiFormHelperText: {
        styleOverrides: {
          root: { marginTop: '3px', marginLeft: '14px', marginRight: '14px' },
        },
      },
    },
  }, esES);
}
